/**
 * Admin SCIM API
 *
 * Covers:
 *   - SCIM configuration status (get / enable / disable)
 *   - Token rotation
 *
 * All routes live under /admin/scim/...
 */

import api from "./index";
import ENDPOINTS from "./endpoints";
import logger from "../services/Logger";

/**
 * Get the SCIM configuration status for the org.
 * Returns null if SCIM is not configured (204).
 * @returns {Promise<Object|null>}
 */
export const getScimConfig = async () => {
  try {
    const response = await api({ method: "GET", url: ENDPOINTS.ADMIN_SCIM_CONFIG });
    if (response.status === 204) return null;
    return response.data;
  } catch (error) {
    logger.error("adminScim", "Error getting SCIM config", { error: error.message });
    throw error;
  }
};

/**
 * Enable SCIM for the org and generate a bearer token.
 * The token is returned once and must be copied immediately.
 * @param {string} provider  "azure_ad" or "okta"
 * @returns {Promise<{ token: string, endpoint: string }>}
 */
export const enableScim = async (provider) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_SCIM_CONFIG,
      data: { provider },
    });
    return response.data;
  } catch (error) {
    logger.error("adminScim", "Error enabling SCIM", { error: error.message });
    throw error;
  }
};

/**
 * Rotate the SCIM bearer token. Old token is invalidated immediately.
 * @returns {Promise<{ token: string }>}
 */
export const rotateScimToken = async () => {
  try {
    const response = await api({ method: "POST", url: ENDPOINTS.ADMIN_SCIM_ROTATE_TOKEN });
    return response.data;
  } catch (error) {
    logger.error("adminScim", "Error rotating SCIM token", { error: error.message });
    throw error;
  }
};

/**
 * Disable SCIM for the org. Existing memberships are NOT removed.
 * @returns {Promise<{ disabled: boolean }>}
 */
export const disableScim = async () => {
  try {
    const response = await api({ method: "DELETE", url: ENDPOINTS.ADMIN_SCIM_CONFIG });
    return response.data;
  } catch (error) {
    logger.error("adminScim", "Error disabling SCIM", { error: error.message });
    throw error;
  }
};
